import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import configuracionService, { ConfiguracionDto } from '../../services/configuracionService';

const displayOrDash = (text: string | null | undefined): string => {
  const s = (text ?? '').trim();
  return s || '-';
};

const truncate = (text: string | null | undefined, max = 60): string => {
  const s = (text ?? '').trim();
  if (!s) return '-';
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const comentarioCellSx = {
  whiteSpace: 'normal' as const,
  wordBreak: 'break-word' as const,
  verticalAlign: 'top' as const,
  lineHeight: 1.5,
  minWidth: 320,
};

const rowKey = (c: ConfiguracionDto): string =>
  `${c.usuario ?? ''}|${c.seccion}|${c.campo}`;

const ParametrosSistema: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<ConfiguracionDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroSeccion, setFiltroSeccion] = useState('');
  const [secciones, setSecciones] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchConfiguraciones = useCallback(
    async (
      pageNum: number = page,
      pageSize: number = rowsPerPage,
      search: string = searchTerm,
      seccion: string = filtroSeccion,
    ) => {
      try {
        setLoading(true);
        const data = await configuracionService.getConfiguraciones({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
          seccion: seccion || undefined,
        });
        setItems(data.items);
        setTotalCount(data.totalCount);
      } catch (error) {
        console.error('Error al cargar parámetros:', error);
        enqueueSnackbar('Error al cargar parámetros del sistema', { variant: 'error' });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchTerm, filtroSeccion, enqueueSnackbar],
  );

  useEffect(() => {
    const loadSecciones = async () => {
      try {
        const list = await configuracionService.getSecciones();
        setSecciones(list);
      } catch (error) {
        console.error('Error al cargar secciones:', error);
      }
    };
    loadSecciones();
    fetchConfiguraciones();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchConfiguraciones(0, rowsPerPage, searchTerm, filtroSeccion);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, filtroSeccion]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchConfiguraciones(newPage, rowsPerPage, searchTerm, filtroSeccion);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchConfiguraciones(0, newRowsPerPage, searchTerm, filtroSeccion);
  };

  const handleEdit = (config: ConfiguracionDto) => {
    const params = new URLSearchParams({
      usuario: config.usuario ?? '',
      seccion: config.seccion,
      campo: config.campo,
    });
    navigate(`/configuraciones/parametros/editar?${params.toString()}`);
  };

  const renderUsuario = (usuario: string | undefined) => {
    const u = (usuario ?? '').trim();
    return u ? u : '(global)';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Parámetros del sistema
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Los parámetros se crean desde el sistema. Aquí solo se editan valor y comentario.
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
              <InputLabel id="filtro-seccion-label">Sección</InputLabel>
              <Select
                labelId="filtro-seccion-label"
                label="Sección"
                value={filtroSeccion}
                onChange={(e) => setFiltroSeccion(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {secciones.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              placeholder="Buscar por sección, campo, valor o comentario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Stack>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando...</Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {items.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No se encontraron parámetros.
                </Typography>
              ) : (
                items.map((config) => (
                  <Card key={rowKey(config)} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {config.seccion} / {config.campo}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Usuario: {renderUsuario(config.usuario)}
                      </Typography>
                      <Typography variant="body2">
                        Valor: {truncate(config.valor, 80)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        Comentario: {displayOrDash(config.comentario)}
                      </Typography>
                      <Stack direction="row" justifyContent="flex-end">
                        <IconButton size="small" onClick={() => handleEdit(config)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Card>
                ))
              )}
            </Stack>
          ) : (
            <TableContainer>
              <Table sx={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Sección</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Campo</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 600, ...comentarioCellSx }}>Comentario</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No se encontraron parámetros.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((config) => (
                      <TableRow key={rowKey(config)} hover>
                        <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{config.seccion}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{config.campo}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{renderUsuario(config.usuario)}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{truncate(config.valor)}</TableCell>
                        <TableCell sx={comentarioCellSx}>{displayOrDash(config.comentario)}</TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleEdit(config)} color="primary">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ParametrosSistema;
